import React, {
  useState,
  cloneElement,
  useImperativeHandle,
  forwardRef,
  ForwardedRef,
} from 'react';

import classNames from 'classnames';
import ReactModal from 'react-modal';

import { SimpleLoader } from './SimpleLoader';

export interface ModalRefProps {
  show: (...args: any[]) => void;
  hide: () => void;
}

type ModalProps = {
  actionFunc: () => void;
  actionText: string;
  cancelText: string;
  disabled?: boolean;
  isDanger?: boolean;
  btnClassName?: string;
  contentClassName?: string;
  toggleTarget?: React.ReactElement;
  children: React.ReactNode;
  loading?: boolean;
  showFooter?: boolean;
  showAction?: boolean;
  loadingText?: string;
  onClose?: () => void;
};

const Modal = forwardRef(
  (
    {
      actionFunc,
      children,
      actionText,
      cancelText,
      disabled = false,
      isDanger = false,
      btnClassName = '',
      contentClassName = '',
      toggleTarget = undefined,
      loading = false,
      showFooter = true,
      showAction = true,
      loadingText = '',
      onClose,
      ...rest
    }: ModalProps,
    ref: ForwardedRef<ModalRefProps>
  ) => {
    const [isOpen, setIsOpen] = useState(false);
    const openModal = (): void => {
      setIsOpen(true);
    };
    const closeModal = (): void => {
      setIsOpen(false);
      onClose?.();
    };
    const handleAction = (): void => {
      actionFunc();
      closeModal();
    };

    useImperativeHandle(ref, () => ({
      show: () => setIsOpen(true),
      hide: () => setIsOpen(false),
    }));

    return (
      <>
        {!!toggleTarget && cloneElement(toggleTarget, { onClick: openModal })}
        <div data-testid="modal" {...rest}>
          <ReactModal
            isOpen={isOpen}
            onRequestClose={closeModal}
            className={classNames('modal', contentClassName)}
            overlayClassName="overlay"
            appElement={document.querySelector('#root') as HTMLElement}
          >
            {children}
            {showFooter && (
              <div className={classNames('flex mt-6', btnClassName || 'ml-auto')}>
                <button type="button" className="btn btn-ghost" onClick={closeModal}>
                  {cancelText}
                </button>
                {showAction && (
                  <button
                    type="button"
                    className={classNames(
                      'btn ml-4 flex items-center',
                      isDanger ? 'btn-danger' : 'btn-secondary'
                    )}
                    disabled={disabled || loading}
                    onClick={handleAction}
                    data-testid="modal-action"
                  >
                    {loading ? (
                      <>
                        <SimpleLoader />
                        {!!loadingText && <span className="ml-2">{loadingText}</span>}
                      </>
                    ) : (
                      actionText
                    )}
                  </button>
                )}
              </div>
            )}
          </ReactModal>
        </div>
      </>
    );
  }
);

export default Modal;
